import { classes } from "components/Utility";
import React, { CSSProperties, ForwardedRef, forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";

export type CodeEditorProps = {
  /** Initial / controlled value. If you pass onChange, this acts like a controlled input. */
  value?: string;
  /** Called when content changes. */
  onChange?: (value: string) => void;
  /** Ace mode
   * seems that we use "html" for markup, "xquery" for xpath, and "xml" for rulesets?
   * json and python are used in a a script editor in the old code too
  */
  mode: "html" | "xquery" | "xml"; // feel free to add more types as needed. i just want to give users guidance into what ME uses currently
  /** Readonly toggle */
  readOnly?: boolean;
  /** Editor container classname */
  className?: string;
  /** Start with active line highlight off (matches your legacy use). */
  highlightActiveLine?: boolean;
  /** Whether to enable word wrap */
  wrap?: boolean;
};

export type CodeEditorRef = {
  setValue: (v: string) => void;
  getValue: () => string;
  getCursorPosition: () => AcePosition;
  insertTextAtCursor: (text: string) => void;
  focus: () => void;
  /** Access the underlying Ace editor (escape hatch). */
  getEditor: () => AceEditor | null;
};

const CodeEditor = forwardRef(function CodeEditor(
  {
    value,
    onChange,
    mode,
    readOnly = false,
    className,
    highlightActiveLine = true,
    wrap = false,
  }: CodeEditorProps,
  ref: ForwardedRef<CodeEditorRef>
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<AceEditor | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let disposed = false;

    ensureAceLoaded()
      .then(() => {
        if (disposed || !containerRef.current) return;
        // Initialize Ace
        const editor = window.ace!.edit(containerRef.current);
        editorRef.current = editor;

        // Base config
        editor.setTheme("ace/theme/monokai");
        editor.getSession().setMode(`ace/mode/${mode}`);
        editor.setReadOnly(readOnly);
        editor.setHighlightActiveLine(!!highlightActiveLine);
        editor.getSession().setUseWrapMode(!!wrap);

        // Set value (avoid moving cursor to end; keep position)
        const initial = value ?? "";
        editor.setValue(initial, -1);

        // Change events
        if (onChange) {
          editor.getSession().on("change", () => {
            onChange(editor.getValue());
          });
        }

        editor.focus();
        setReady(true);
      })
      .catch((e) => {
        console.error(e);
      });

    return () => {
      disposed = true;
      if (editorRef.current) {
        try {
          editorRef.current.destroy();
          editorRef.current.container && (editorRef.current.container.textContent = "");
        } catch {
          // ignore
        } finally {
          editorRef.current = null;
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!ready || !editorRef.current) return;
    editorRef.current.getSession().setMode(`ace/mode/${mode}`);
  }, [mode, ready]);

  useEffect(() => {
    if (!ready || !editorRef.current) return;
    editorRef.current.setReadOnly(!!readOnly);
  }, [readOnly, ready]);

  useEffect(() => {
    if (!ready || !editorRef.current) return;
    editorRef.current.setHighlightActiveLine(!!highlightActiveLine);
  }, [highlightActiveLine, ready]);

  useEffect(() => {
    if (!ready || !editorRef.current) return;
    editorRef.current.getSession().setUseWrapMode(!!wrap);
  }, [wrap, ready]);

  // // Controlled value behavior
  // useEffect(() => {
  //   if (!ready || !editorRef.current) return;
  //   if (typeof value === "string" && value !== editorRef.current.getValue()) {
  //     const pos = editorRef.current.getCursorPosition();
  //     editorRef.current.setValue(value, -1);
  //     editorRef.current.moveCursorToPosition(pos);
  //   }
  // }, [value, ready]);

  // API exposed to parent thru useref
  useImperativeHandle(
    ref,
    (): CodeEditorRef => ({
      setValue: (v: string) => {
        if (!editorRef.current) return;
        const pos = editorRef.current.getCursorPosition();
        editorRef.current.setValue(v ?? "", -1);
        editorRef.current.moveCursorToPosition(pos);
      },
      getValue: () => (editorRef.current ? editorRef.current.getValue() : ""),
      getCursorPosition: () => (editorRef.current ? editorRef.current.getCursorPosition() : { row: 0, column: 0 }),
      insertTextAtCursor: (text: string) => {
        if (!editorRef.current) return;
        editorRef.current.insert(text);
      },
      focus: () => {
        if (!editorRef.current) return;
        editorRef.current.focus();
      },
      getEditor: () => editorRef.current,
    }),
    []
  );

  return <div ref={containerRef} className={classes("code-editor", className)} />;
});

const ACE_SRC = "https://testanywhere.phoenixsuite.com/js/ace/ace.js";
const ACE_SCRIPT_ID = "phoenix-ace-script";

/** Minimal typings to avoid bringing in @types/ace */
type AceAny = any;
type AceEditor = any;
type AcePosition = { row: number; column: number };

declare global {
  interface Window {
    ace?: AceAny;
  }
}

/** Singleton script loader so we only fetch Ace once */
let aceLoaderPromise: Promise<void> = null;
export const ensureAceLoaded = (): Promise<void> => {
  // Already present (either previously injected or bundled somehow)
  if (window.ace) return Promise.resolve();

  // Script tag already in DOM (added by another component earlier)
  const existing = document.getElementById(ACE_SCRIPT_ID) as HTMLScriptElement;
  if (existing) {
    if (window.ace) return Promise.resolve();
    // If it exists but ace isn't ready yet, attach to its load event:
    if (!aceLoaderPromise) {
      aceLoaderPromise = new Promise<void>((resolve, reject) => {
        existing.addEventListener("load", () => resolve(), { once: true });
        existing.addEventListener("error", () => reject(new Error("Failed to load Ace")), { once: true });
      });
    }
    return aceLoaderPromise;
  }

  // Inject a new script tag
  aceLoaderPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.id = ACE_SCRIPT_ID;
    s.src = ACE_SRC;
    s.async = true;
    s.crossOrigin = "anonymous";
    s.addEventListener("load", () => resolve(), { once: true });
    s.addEventListener("error", () => reject(new Error("Failed to load Ace")), { once: true });
    document.head.appendChild(s);
  });

  return aceLoaderPromise;
};

export default CodeEditor;
